import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  TextField,
  Card,
  Typography,
  FormHelperText,
  CircularProgress,
  Box,
  Stack,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Checkbox,
  ListSubheader,
  Tooltip,
  Popover,
  debounce,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDButton from "components/MDButton";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { IsheadingExistBlog, openSnackbar, uploadImage } from "../../redux/slices/common";
import { getData, slugRegex } from "utilities/common";
import CommonQuillEditor from "common/editorBlog";
import FAQFormList from "./faq-view";
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import {
  getBlogCategories,
  createBlogAuthor,
  createBlogCategory,
  deleteBlogAuthor,
  deleteBlogCategory,
  createBlog,
  findBlogById,
  updateBlog,
  getBlogList,
  migrateAuthor
} from "../../redux/apis/blogs";
import DeleteIcon from "common/customIcons/delete";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../redux/slices/modalSlice";
import AltImageSection from "layouts/strains/addStrain/component/ImageUpdate";
import { statesData } from "utilities/common/Cities";
import { convertBrandNameToSlug } from "utilities/common";
import InfiniteScrollListBlog from 'common/component/Hyperwolf/InfiniteScrollListBlog';
import { DatePicker, LocalizationProvider, MobileTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import AngleRightIcon from "common/customIcons/angle_right"
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { blogMetaPrompt } from "prompt";
import { convertAndExtractMeta } from 'utilities/common';

dayjs.extend(utc);
dayjs.extend(timezone);

const BlogInitialState = {
  status: true,
  postTitle: "",
  slug: "",
  categories: ["Cannabis Education"],
  author: "admin",
  excerpt: "",
  tags: [],
  // Array-based content editors (max 5 in UI)
  content: [{ _id: "content-0", text1: "" }],
  image: {},
  listingImage: {},
  bannerMobile: "",
  metaTitle: "",
  metaDescription: "",
  faqs: [{ question: "", answer: "" }],
  faqStatus: false,
  state: "",
  readTime: 8,
  relatedBlogStatus: false,
  relatedBlogData: [],
  scheduleBlogStatus: false,
  scheduleDate: null,
  scheduleTime: null
};


const useBlogForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { blogId } = useParams();
  const { isUploading } = useSelector((s) => s.common);
  const authorsFromStore = useSelector((s) => s?.blogs?.authors || []);
  const { loading, creatingBlog, updatingBlog, fetchingBlogs } = useSelector((s) => s?.blogs);

  const categoriesFromStore = useSelector((s) => s.blogs?.categories || []);
  const [blogData, setBlogData] = useState(BlogInitialState);
  const [publishingOption, setPublishingOption] = useState("published"); // "published" | "schedule"
  const [errors, setErrors] = useState({});
  const currentUploadRef = useRef(null);
  const [localAuthors, setLocalAuthors] = useState([]);
  const [localCategories, setLocalCategories] = useState([]);
  const [openAuthorDialog, setOpenAuthorDialog] = useState(false);
  const [newAuthorName, setNewAuthorName] = useState("");
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isSlugTrue, setIsSlugTrue] = useState(false)
  const [error, setError] = useState(null);
  const [blogPublishData, setBlogPublishData] = useState("Publish")
  const [spinStateLoader, setSpinStateLoader] = useState({
    loader: false, submiType: ""
  })
  const seoCheckPlatform = useMemo(() => {
    try {
      const raw = getData("adminsMode");
      return raw ? JSON.parse(raw) : "hemp";
    } catch {
      return "hemp";
    }
  }, []);

  useEffect(() => {
    dispatch(migrateAuthor({ skip: 0, limit: 100 })).unwrap().then(res => {
      setLocalAuthors(res?.data)
    }).catch(err => {
    })
    // Fetch initial data when component mounts
    // dispatch(getBlogAuthors());
    dispatch(getBlogCategories());
  }, [dispatch]);
  useEffect(() => {
    if (categoriesFromStore?.length) {
      setLocalCategories(categoriesFromStore);
    }
  }, [categoriesFromStore]);

  useEffect(() => {
    if (authorsFromStore?.length) {
      setLocalAuthors(authorsFromStore);
    }
  }, [authorsFromStore]);
  const debounceRef = useRef(null);

  const handlePostTitleChange = (value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      dispatch(IsheadingExistBlog({ platform: seoCheckPlatform, heading: value }))
        .unwrap()
        .then(() => {
          setErrors({ postTitle: "" });
        })
        .catch((err) => {
          setErrors({ postTitle: err?.message || "An error occurred" });
        });
    }, 500); // 500ms delay
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "postTitle") {
      handlePostTitleChange(value);
    }

    setBlogData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSlugChange = (e) => {
    setIsSlugTrue(true)
    setBlogData((p) => ({ ...p, slug: slugRegex(e.target.value) }));
  };

  const handleTagsChange = (e) => {
    const value = e.target.value;
    // Handle case where only comma is entered
    if (value.trim() === ',') {
      setBlogData((p) => ({ ...p, tags: [] }));
      return;
    }
    // Split by comma and trim whitespace from each tag
    const tagsArray = value.split(',').map(tag => tag.trim());
    // Filter out empty strings from the array
    const filteredTags = tagsArray.filter(tag => tag.length > 0);
    setBlogData((p) => ({
      ...p,
      tags: filteredTags
    }));
  };

  const handleBannerUpload = (type) => async (e) => {
    if (!e.target.files?.length) return;

    // Validate file type
    const file = e.target.files[0];

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      dispatch(
        openSnackbar({
          content: 'Only image files (JPEG, PNG, GIF, WEBP) are allowed',
          color: "error",
          icon: "warning"
        })
      );
      return;
    }

    currentUploadRef.current = type;
    const body = new FormData();
    body.append("filePath", file);
    try {
      const res = await dispatch(uploadImage(body)).unwrap();
      setBlogData((p) => ({ ...p, [type]: res.fileUrl }));
    } catch (err) {
      dispatch(
        openSnackbar({ content: err.message, color: "error", icon: "warning" })
      );
    }
  };

  const handleChangeFAQ = (value, field, idx) => {
    setBlogData((p) => ({
      ...p,
      faqs: p.faqs.map((f, i) => (i === idx ? { ...f, [field]: value } : f)),
    }));
  };

  const addFAQHandler = () =>
    setBlogData((p) => ({ ...p, faqs: [...p.faqs, { question: "", answer: "" }] }));

  const handleRemoveFAQ = (idx) =>
    setBlogData((p) => ({ ...p, faqs: p.faqs.filter((_, i) => i !== idx) }));

  const handleAuthorChange = (e) => {
    if (e.target.value === "add_new_author") {
      setOpenAuthorDialog(true);
      setBlogData((p) => ({ ...p, author: { name: "", _id: "", slug: "" } }));
    } else {
      // Find the selected author object from localAuthors
      const selectedAuthor = localAuthors?.find(a => a.name === e.target.value);
      setBlogData((p) => ({
        ...p,
        author: selectedAuthor ? {
          name: selectedAuthor.name,
          _id: selectedAuthor._id || selectedAuthor.id || "",
          slug: selectedAuthor?.slug || ""
        } : { name: e.target.value, _id: "", slug: "" }
      }));
    }
  };
  const handleCategoryChange = (e) => {
    const { value } = e.target;
    if (value === "add_new_category") {
      setOpenCategoryDialog(true);
    } else {
      setBlogData((p) => ({
        ...p,
        categories: Array.isArray(value) ? value : [value]
      }));
    }
  };

  const handleAddNewAuthor = async () => {
    const trimmedName = newAuthorName.trim();
    if (!trimmedName) {
      dispatch(
        openSnackbar({
          content: "Author name cannot be empty",
          color: "error",
          icon: "warning",
        })
      );
      return;
    }
    const newAuthor = {
      id: `temp-author-${Date.now()}`,
      name: newAuthorName.trim(),
    };
    // setLocalAuthors((prev) => [...prev, newAuthor]);

    setNewAuthorName("");

    const handleAuthorCreation = async () => {
      try {
        const newCategory = await dispatch(createBlogAuthor({ name: newAuthorName.trim() })).unwrap();

        if (!newCategory?.authors) {
          throw new Error('Invalid category response');
        }

        setLocalAuthors(newCategory.authors);
        setBlogData((p) => ({ ...p, author: newAuthor.name }));
        dispatch(
          openSnackbar({
            content: `Author "${newAuthor.name}" added`,
            color: "success",
            icon: "check",
          })
        );
        setOpenAuthorDialog(false);


      } catch (error) {
        dispatch(
          openSnackbar({
            content: error.message || "Failed to create category. Please try again.",
            color: "error",
            icon: "warning",
          })
        );
      }
    };

    handleAuthorCreation();
  };

  const handleDeleteAuthor = async (authorSlug) => {
    try {
      // Show loading state in modal
      dispatch(setModalLoader(true));

      // Delete author from backend
      await dispatch(deleteBlogAuthor(authorSlug)).unwrap();

      // Update local authors list by removing deleted author
      setLocalAuthors((prev) => prev.filter((a) => a.slug !== authorSlug));

      // If deleted author was selected in form, clear the selection
      if (blogData.author === authorSlug) {
        setBlogData((p) => ({ ...p, author: "" }));
      }

      // Show success notification
      dispatch(
        openSnackbar({
          content: "Author deleted successfully",
          color: "success",
          icon: "check",
        })
      );
      dispatch(closeCustomModalDialog());

    } catch (error) {
      // Show error notification with error message
      dispatch(
        openSnackbar({
          content: error.message || "Failed to delete author",
          color: "error",
          icon: "warning",
        })
      );
    } finally {
      // Ensure modal loader is turned off in all cases
      dispatch(setModalLoader(false));

    }
  };

  const handleAddNewCategory = () => {
    if (!newCategoryName.trim()) {
      dispatch(
        openSnackbar({
          content: "Category name cannot be empty",
          color: "error",
          icon: "warning",
        })
      );
      return;
    }

    const handleCategoryCreation = async () => {
      try {
        const newCategory = await dispatch(createBlogCategory({ name: newCategoryName.trim() })).unwrap();

        if (!newCategory?.categories) {
          throw new Error('Invalid category response');
        }

        setLocalCategories(newCategory.categories);
        setNewCategoryName("");

        dispatch(
          openSnackbar({
            content: `Category "${newCategoryName.trim()}" added successfully`,
            color: "success",
            icon: "check",
          })
        );
        setBlogData((p) => ({ ...p, categories: [...p?.categories, newCategoryName.trim()] }));
        setOpenCategoryDialog(false)

      } catch (error) {
        dispatch(
          openSnackbar({
            content: error.message || "Failed to create category. Please try again.",
            color: "error",
            icon: "warning",
          })
        );
      }
    };

    handleCategoryCreation();
  };

  const handleDeleteCategory = async (categorySlug) => {
    try {
      dispatch(setModalLoader(true));
      await dispatch(deleteBlogCategory(categorySlug)).unwrap();
      setLocalCategories((prev) => prev.filter((c) => c?.slug !== categorySlug));
      if (blogData.categories?.some(c => c?.slug === categorySlug)) {
        setBlogData((p) => ({
          ...p,
          categories: p?.categories.filter((c) => c?.slug !== categorySlug)
        }));
      }

      dispatch(
        openSnackbar({
          content: "Category deleted successfully",
          color: "success",
          icon: "check",
        })
      );
      dispatch(closeCustomModalDialog());
    } catch (error) {
      let errorMessage = "Failed to delete category";
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      dispatch(
        openSnackbar({
          content: errorMessage,
          color: "error",
          icon: "warning",
        })
      );
      dispatch(closeCustomModalDialog());
    }
  };

  const validate = () => {
    const temp = {};
    if (!blogData.postTitle) temp.postTitle = "Blog title is required";
    // if (!blogData.categories?.length) temp.categories = "At least one category is required";
    // if (blogData.author === "admin") temp.author = "Author is required";
    if (!blogData.image?.url) temp.image = "Banner Image is required";
    // if (!blogData.listingImage?.url) temp.listingImage = "Listing Image is required";
    if (!blogData.metaTitle) temp.metaTitle = "Meta title is required";
    if (!blogData.metaDescription) temp.metaDescription = "Meta description is required";
    if (!blogData.content?.[0]?.text1?.trim()) temp.content0 = "Content is required";
    if (blogData.scheduleBlogStatus) {
      if (!blogData.scheduleDate) temp.scheduleDate = "Schedule date & time is required";
      if (!blogData.scheduleTime) temp.scheduleTime = "Schedule date & time is required";
    }

    setErrors(temp);
    return !Object.keys(temp).length;
  };

  useEffect(() => {
    if (blogData?.scheduleDate && blogData?.scheduleTime) {
      setErrors((prev) => ({ ...prev, scheduleDate: "", scheduleTime: "" }))
    }
  }, [blogData])

  const handleSubmit = (submiType = 'published') => {
    if (!validate()) return;
    const allowedCategoryNames = localCategories.map(cat => cat.name);
    const formattedData = {
      title: blogData.postTitle,
      description: blogData.excerpt,
      category: blogData.categories?.length ? blogData.categories.filter(category =>
        allowedCategoryNames.includes(category)
      ) : ["cannabis education"],
      author: blogData.author === "admin" ? {} : blogData.author,
      // content: (blogData.content?.length ? blogData.content : [{}]).map((item, index) => {
      //   const textValue = item?.[`text${index + 1}`] ?? "";

      //   return { textValue };
      // }),
      content: (blogData.content?.length ? blogData.content : []).map((item, index) => {
        // Extract the text value from the object (text1, text2, etc.)
        const textKey = `text${index + 1}`;
        return item?.[textKey] || "";
      }),
      image: blogData.image,
      listingImage: blogData.listingImage,
      status: submiType === "schedule" ? "draft" : submiType,
      metaTitle: blogData.metaTitle,
      metaDescription: blogData.metaDescription,
      faq: blogData.faqs.map(faq => ({
        question: faq.question,
        answer: faq.answer
      })),
      faqStatus: blogData.faqStatus,
      state: blogData?.state,
      readTime: Number(blogData?.readTime),
      relatedBlogStatus: blogData?.relatedBlogStatus,
      relatedBlogData: blogData?.relatedBlogData,
    };
    if (isSlugTrue) {
      formattedData.slug = blogData.slug;
    }
    if (submiType === "schedule") {
      formattedData.scheduleBlogStatus = true;
      formattedData.scheduleDate = dayjs(blogData?.scheduleDate).format('YYYY-MM-DD')
      formattedData.scheduleTime = blogData.scheduleTime
    }
    setSpinStateLoader({ loader: true, submiType })
    const action = blogId
      ? dispatch(updateBlog({ id: blogId, data: formattedData }))
      : dispatch(createBlog(formattedData));
    action
      .unwrap()
      .then((res) => {
        setSpinStateLoader({ loader: false, submiType })
        const message = res?.message;
        dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
        navigate("/manages/blog");

      })
      .catch((error) => {
        setSpinStateLoader({ loader: false, submiType })
        const message = error?.message;
        dispatch(openSnackbar({ content: error.message || message, color: "error", icon: "warning" }));
      });
  };



  useEffect(() => {
    if (blogId) {
      const fetchBlogData = async () => {
        try {
          const { blog: blogDetails } = await dispatch(findBlogById(blogId)).unwrap();

          const normalizeContent = (details) => {
            const now = Date.now();

            const getTextValue = (item) => {
              if (!item) return "";
              if (typeof item === "string") return item;
              if (typeof item === "object") {
                if (typeof item.text === "string") return item.text;
                // Prefer keys shaped like text1/text2/...
                const textKey = Object.keys(item).find(
                  (k) => /^text\d+$/i.test(k) || k.toLowerCase().startsWith("text")
                );
                if (textKey && typeof item[textKey] === "string") return item[textKey];

                // Fallback to first string value
                const firstStringKey = Object.keys(item).find((k) => typeof item[k] === "string");
                if (firstStringKey) return item[firstStringKey];
              }
              return "";
            };

            const makeItem = (text, index, existingId) => ({
              _id: existingId || `content-${now}-${index}`,
              [`text${index + 1}`]: typeof text === "string" ? text : (text ?? "")
            });

            const trimTrailingEmpty = (items) => {
              if (!items?.length) return [makeItem("", 0)];
              let lastFilled = -1;
              for (let i = items.length - 1; i >= 0; i--) {
                if (String(getTextValue(items[i]) || "").trim()) {
                  lastFilled = i;
                  break;
                }
              }
              const next = lastFilled >= 0 ? items.slice(0, lastFilled + 1) : [items[0]];
              return next?.length ? next : [makeItem("", 0)];
            };

            const raw = details?.content;
            if (Array.isArray(raw)) {
              const items = raw.map((item, i) =>
                makeItem(getTextValue(item), i, item && typeof item === "object" ? item._id : undefined)
              );
              return trimTrailingEmpty(items);
            }

            if (typeof raw === "string") {
              return trimTrailingEmpty([makeItem(raw, 0)]);
            }

            // Dynamic content processing: content1..content50
            const legacy = [];
            for (let i = 1; i <= 50; i++) {
              const contentKey = `content${i}`;
              if (details?.[contentKey]) {
                legacy.push(makeItem(details[contentKey], i - 1));
              }
            }

            if (legacy.length === 0) {
              return trimTrailingEmpty([makeItem("", 0)]);
            }

            return trimTrailingEmpty(legacy);
          };

          const updatedBlogDetails = {
            status: blogDetails?.status === 'published' || true,
            postTitle: blogDetails?.title || "",
            slug: blogDetails?.slug || "",
            categories: blogDetails?.category || [],
            author: blogDetails?.author || "",
            excerpt: blogDetails?.description || "",
            tags: blogDetails?.tags || [],
            content: normalizeContent(blogDetails),
            image: blogDetails?.image || {},
            listingImage: blogDetails?.listingImage || {},
            bannerMobile: "",
            metaTitle: blogDetails?.metaTitle || "",
            metaDescription: blogDetails?.metaDescription || "",
            faqs: blogDetails?.faq?.length ? blogDetails?.faq : [{ question: "", answer: "" }],
            faqStatus: blogDetails?.faqStatus,
            state: blogDetails?.state || "",
            readTime: blogDetails?.readTime || "",
            relatedBlogStatus: blogDetails?.relatedBlogStatus,
            relatedBlogData: blogDetails?.relatedBlogData || [],
            scheduleBlogStatus: blogDetails?.scheduleBlogStatus || false,
            scheduleDate: blogDetails?.scheduleDate || null,
            scheduleTime: blogDetails?.scheduleTime

          };
          // if (blogDetails?.scheduleBlogStatus) {
          //   setPublishingOption("schedule");
          // }
          // else {
          //   setPublishingOption("published");
          // }

          setBlogPublishData(blogDetails?.scheduleStatus ? "Publish" : blogDetails?.status === "published" ? "Update" : "Publish")
          setBlogData(updatedBlogDetails);
        } catch (error) {

          dispatch(
            openSnackbar({
              content: error.message || "Failed to fetch blog data",
              color: "error",
              icon: "warning",
            })
          );
        }
      };
      fetchBlogData();
    }
  }, [blogId, dispatch]);

  return {
    blogData,
    errors,
    isUploading,
    currentUploadRef,
    localAuthors,
    localCategories,
    openAuthorDialog,
    newAuthorName,
    openCategoryDialog,
    newCategoryName,
    handleChange,
    handleSlugChange,
    handleTagsChange,
    handleBannerUpload,
    handleChangeFAQ,
    addFAQHandler,
    handleRemoveFAQ,
    handleAuthorChange,
    handleCategoryChange,
    handleAddNewAuthor,
    handleAddNewCategory,
    handleSubmit,
    setNewAuthorName,
    setNewCategoryName,
    setOpenAuthorDialog,
    setOpenCategoryDialog,
    handleDeleteAuthor,
    handleDeleteCategory,
    setBlogData,
    setPublishingOption,
    publishingOption,
    loading,
    creatingBlog,
    updatingBlog,
    fetchingBlogs,
    blogPublishData,
    spinStateLoader
  };
};

export default function AddBlogForm() {
  const {
    blogData,
    errors,
    isUploading,
    localAuthors,
    localCategories,
    openCategoryDialog,
    newCategoryName,
    handleChange,
    handleSlugChange,
    handleChangeFAQ,
    addFAQHandler,
    handleRemoveFAQ,
    handleAuthorChange,
    handleCategoryChange,
    handleAddNewCategory,
    handleSubmit,
    setNewCategoryName,
    setOpenCategoryDialog,
    handleDeleteCategory,
    loading,
    updatingBlog,
    creatingBlog,
    setBlogData,
    setPublishingOption,
    publishingOption,
    blogPublishData,
    spinStateLoader
  } = useBlogForm();
  const { blogId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const states = statesData
  const [stateSearch, setStateSearch] = useState("");
  const { blogList } = useSelector((state) => state.blogs)
  const { currentBlog } = useSelector((state) => state.blogs)
  const [copyStatus, setCopyStatus] = useState('')
  const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
  // "published" | "schedule"
  const [isPromptLoading, setPromptIsLoading] = useState(false)


  const handleGenerate = useCallback(async () => {
    setPromptIsLoading(true);
    setBlogData(prev => ({
      ...prev,
      // description: '',
      metaTitle: '',
      metaDescription: ''
    }));

    try {
      const prompt = blogMetaPrompt(blogData?.postTitle);
      const payload = { prompt: prompt };

      const res = await fetch(`${process.env.REACT_APP_STILO_API_BASE_URL}/api/v1/generate/description`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_API_AUTH_KEY
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`API Error (${res.status}): ${errorText || res.statusText}`);
      }

      const reader = res.body.getReader();

      const decoder = new TextDecoder("utf-8");

      let fullContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;

        // Optional: real-time update

        const finalResult = convertAndExtractMeta(fullContent);

        setBlogData((prev) => ({
          ...prev,
          // description: finalResult.content,
          metaTitle: finalResult.metaTitle || prev.metaTitle,
          metaDescription: finalResult.metaDescription,
        }));
      }
      const finalResult = convertAndExtractMeta(fullContent);
      setBlogData((prev) => ({
        ...prev,
        // description: finalResult.content,
        metaTitle: finalResult.metaTitle || prev.metaTitle,
        metaDescription: finalResult.metaDescription,
      }));

    } catch (error) {
      dispatch(openSnackbar({
        content: `Failed to generate meta content: ${error.message}`,
        color: "error",
        icon: "warning"
      }));
    } finally {
      setPromptIsLoading(false);
    }
  }, [blogData?.postTitle]);


  const handleClick = (event) => {
    setPublishingOption("schedule");
    setBlogData((prev) => ({ ...prev, scheduleBlogStatus: true }));
    // if (blogData?.scheduleBlogStatus) {
    //   setPublishingOption("schedule");
    //   setBlogData((prev) => ({ ...prev, scheduleBlogStatus: true }));
    // }
    // else {
    //   setPublishingOption("published");
    // }
    setScheduleAnchorEl(event.currentTarget);
  };



  const handleScheduleClose = () => {
    setBlogData((prev) => ({ ...prev, scheduleBlogStatus: false }));
    setScheduleAnchorEl(null);
  };

  const openSchedulePopover = Boolean(scheduleAnchorEl);
  const schedulePopoverId = openSchedulePopover ? "schedule-popover" : undefined;

  const handleCopyLink = () => {
    if (currentBlog?.blog?.canonical) {
      navigator.clipboard.writeText(currentBlog?.blog?.canonical);
      setCopyStatus('Copied');
      setTimeout(() => setCopyStatus(''), 2000);
    }
  };

  useEffect(() => {
    if (!blogId) {
      if (blogData?.postTitle) {
        const slug = convertBrandNameToSlug(blogData?.postTitle)
        setBlogData((prev) => ({ ...prev, slug }))
      }
    }

  }, [blogData?.postTitle, blogId])

  useEffect(() => {
    dispatch(getBlogList()).unwrap().then(() => {
    }).catch((error) => {
    })
  }, [])

  const blogSelectHandler = (selectedBlogs = []) => {

    // The InfiniteScrollListBlog now returns objects with blogId and title directly
    // No need to search and map - just use the data as-is
    setBlogData((prev) => ({ ...prev, relatedBlogData: selectedBlogs }));
  };

  const optionsList = useMemo(() => {
    let blogs = [];
    if (Array.isArray(blogList)) {
      blogs = blogList;
    } else if (Array.isArray(blogList?.blogs)) {
      blogs = blogList.blogs;
    } else if (Array.isArray(blogList?.data)) {
      blogs = blogList.data;
    } else if (blogList && typeof blogList === 'object') {
      blogs = Object.values(blogList).filter(x => x && typeof x === 'object' && ('blogId' in x || '_id' in x));
    }

    return blogs.map((blog) => {
      return {
        value: blog?._id || blog?.id || blog?.blogId,
        label: blog?.title
      };
    }) || [];
  }, [blogList]);

  const maxContentEditors = 50;

  const extractTextValueFromItem = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    if (typeof item === "object") {
      if (typeof item.text === "string") return item.text;
      const textKey = Object.keys(item).find((k) => /^text\d+$/i.test(k));
      if (textKey && typeof item[textKey] === "string") return item[textKey];
      const firstStringKey = Object.keys(item).find((k) => typeof item[k] === "string");
      if (firstStringKey) return item[firstStringKey];
    }
    return "";
  };

  // Ensures each item is shaped like { _id, text{index+1}: string }.
  const normalizeContentItems = (items) => {
    const now = Date.now();
    const safeItems = Array.isArray(items) ? items : [];
    return safeItems.map((item, index) => ({
      _id: item?._id || `content-${now}-${index}`,
      [`text${index + 1}`]: extractTextValueFromItem(item)
    }));
  };

  const addContentField = () => {
    setBlogData((prev) => {
      const curr = Array.isArray(prev.content) ? prev.content : [];
      if (curr.length >= maxContentEditors) return prev;
      const nextIndex = curr.length;
      return {
        ...prev,
        content: normalizeContentItems([
          ...curr,
          { _id: `content-${Date.now()}-${Math.random().toString(16).slice(2)}`, [`text${nextIndex + 1}`]: "" }
        ])
      };
    });
  };

  const removeContentField = (indexToRemove) => {
    setBlogData((prev) => {
      const curr = Array.isArray(prev.content) ? prev.content : [];
      if (curr.length <= 1) return prev; // keep at least one editor
      return { ...prev, content: normalizeContentItems(curr.filter((_, i) => i !== indexToRemove)) };
    });
  };

  // Update content field (index-based)
  const updateContentField = (indexToUpdate, value) => {
    setBlogData((prev) => {
      const curr = Array.isArray(prev.content) ? prev.content : [];
      const next = curr.map((item, i) => {
        if (i !== indexToUpdate) return item;
        const textKey = `text${indexToUpdate + 1}`;
        return { ...item, [textKey]: value };
      });
      return {
        ...prev,
        content: normalizeContentItems(next)
      };
    });
  };


  const onContentDragEnd = (result) => {
    const { source, destination } = result || {};
    if (!destination) return;
    if (source.index === destination.index) return;

    setBlogData((prev) => {
      const curr = Array.isArray(prev.content) ? prev.content : [];
      const next = [...curr];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return { ...prev, content: normalizeContentItems(next) };
    });
  };

  useEffect(() => {
    setBlogData((prev) => ({ ...prev, scheduleBlogStatus: false }));
  }, []);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
        <Grid item xs={12} sm={2}>
          <Box display="flex" alignItems="center" color='text.main' lineHeight={1}>
            <span style={{ cursor: 'pointer', display: "flex", alignItems: "center" }} onClick={() => { navigate('/manages/blog') }}>
              <AngleRightIcon fontSize="14px" />
              <Typography variant='h6' fontWeight="semibold" ml={.75}>{blogId ? 'Blog' : 'Blog'}</Typography>
            </span>
          </Box>
        </Grid>
        <Grid item xs={12} sm={10}>
          <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
            <Box display="flex" alignItems="center" justifyContent="end" gap={2}>
              {blogId && currentBlog?.blog?.canonical && (
                <Box
                  display="flex"
                  sx={{ cursor: "pointer" }}
                  alignItems="center"
                  color="text.main"
                  lineHeight={1}
                  gap={1}
                  style={{ position: "relative" }}
                >
                  <Tooltip title={copyStatus ? copyStatus : "Copy"} arrow>
                    <IconButton
                      size="small"
                      onClick={handleCopyLink}
                      sx={{ color: 'primary.main' }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Typography
                    variant="body"
                    component="a"
                    href={`${currentBlog?.blog?.canonical}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none", color: "inherit" }}
                    style={{
                      borderRadius: "26px",
                      color: "#0163FF",
                      fontSize: "14px",
                      fontWeight: "400",
                      border: "1px solid #0163FF",
                      padding: "10px 16px",
                    }}
                  >{`${currentBlog?.blog?.canonical}`}</Typography>

                </Box>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} style={{ justifyContent: "end" }} spacing={2}>

                <MDButton
                  variant="outlined"
                  color="dark"
                  sx={{ minWidth: 160 }}
                  onClick={() => { handleSubmit('draft'), setBlogData((prev) => ({ ...prev, scheduleBlogStatus: false })) }}
                  disabled={spinStateLoader?.loader && spinStateLoader?.submiType === "draft"}
                >
                  {(spinStateLoader?.loader && spinStateLoader?.submiType === "draft") ? (
                    <CircularProgress size={24} />
                  ) : blogId ? 'Draft' : 'Draft'}
                </MDButton>
                <MDButton
                  variant='contained'
                  color='primary'
                  onClick={() => { handleSubmit('published'), setBlogData((prev) => ({ ...prev, scheduleBlogStatus: false })) }}
                  sx={{ minWidth: 160 }}
                  disabled={spinStateLoader?.loader && spinStateLoader?.submiType === "published"}

                >
                  {(spinStateLoader?.loader && spinStateLoader?.submiType === "published") ? <CircularProgress size={24} />
                    : blogPublishData}
                </MDButton>
                <MDButton
                  variant='contained'
                  color='primary'
                  disabled={spinStateLoader?.loader&& spinStateLoader?.submiType === "schedule"}
                  sx={{ minWidth: 160 }}
                  onClick={handleClick}
                >
                  {(spinStateLoader?.loader&& spinStateLoader?.submiType === "schedule") ?
                    <CircularProgress size={24} />
                    : "Schedule"}
                </MDButton>
                <Popover
                  id={schedulePopoverId}
                  open={openSchedulePopover}
                  anchorEl={scheduleAnchorEl}
                  onClose={handleScheduleClose}
                  PaperProps={{
                    sx: {
                      borderRadius: 3,
                      overflow: "hidden",
                      backgroundColor: "#ffffff",
                      boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                      border: "1px solid rgba(17,24,39,0.08)",
                      zIndex: 1000,
                    },
                  }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box sx={{ width: { xs: 320, sm: 520 } }}>
                   

                    <Box p={2}>
                    

                      {publishingOption === "published"
                        ?
                        <Typography sx={{ mt: 2.25, color: "#374151", fontSize: 15 }}>
                          This page will be published immediately.
                        </Typography>
                        :
                        <Box pt={3} display="flex" gap={1}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              label="Schedule Date"
                              value={blogData?.scheduleDate ? dayjs(blogData.scheduleDate) : null}
                              onChange={(date) => setBlogData((prev) => ({ ...prev, scheduleDate: date }))}

                            />
                            <MobileTimePicker
                              label="Schedule Time"
                              value={blogData?.scheduleTime ? dayjs(blogData.scheduleTime, 'HH:mm') : null}
                              onChange={(time) => {
                                setBlogData((prev) => ({
                                  ...prev,
                                  scheduleTime: time ? time.format('HH:mm') : null
                                }));
                              }}
                              format="HH:mm"
                              ampm={false}  // Set to true if you want AM/PM format
                            />
                          </LocalizationProvider>
                        </Box>
                      }

                      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
                        <Button
                          onClick={() => handleScheduleClose(publishingOption)}
                          variant="outlined"
                          color="inherit"
                          sx={{ borderRadius: "999px", px: 3, fontSize: 14, color: "#000000", borderColor: "#000000", minWidth: "120px" }}
                        >
                          Close
                        </Button>
                        <Button
                          onClick={() => handleSubmit(publishingOption)}
                          variant="contained"
                          color="primary"
                          sx={{ borderRadius: "999px", px: 3, fontSize: 14, minWidth: "120px" }}
                        >
                          {blogId ? "Scheduled" : "Schedule"}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Popover>
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Box className="scoll-area-box">
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>


          {/* -------------- META SECTION -------------- */}

          <Grid container spacing={2.5}>
            <Grid item xs={12}>

              <Grid item xs={12} md={12} lg={12}>
                <TextField
                  label="Blog Title (H1)*"
                  fullWidth
                  name="postTitle"
                  value={blogData?.postTitle || ''}
                  onChange={handleChange}
                  error={Boolean(errors.postTitle)}
                  helperText={errors.postTitle}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
            <Typography variant="h5" fontWeight="semibold">
              {/* {blogId ? 'Update Blog' : 'Add Blog'} */}
              Meta Properties
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" fontWeight="medium" color="#2E2E3A">
                Generate Meta Content
              </Typography>

              <MDButton
                variant="contained"
                color="primary"
                size="small"
                onClick={handleGenerate}
                disabled={isPromptLoading || !blogData?.postTitle}
                startIcon={!isPromptLoading && <SmartToyIcon />} // Show icon only when not loading
              >
                {isPromptLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Generate'
                )}
              </MDButton>
            </Box>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label="Meta Title*"
                fullWidth
                name="metaTitle"
                value={blogData.metaTitle}
                onChange={handleChange}
                multiline
                maxRows={3}
                error={Boolean(errors.metaTitle)}
                helperText={errors.metaTitle}
              />
              <span className='drag_drop'>
                Character Count: {blogData?.metaTitle?.length || 0} <span>(Recommended Characters: 60)</span>
              </span>
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                label="Meta Description*"
                fullWidth
                name="metaDescription"
                value={blogData.metaDescription}
                onChange={handleChange}
                // multiline
                rows={4}
                className="form-input"
                variant="outlined"
                error={Boolean(errors.metaDescription)}
                helperText={errors.metaDescription}
              />
              <span className='drag_drop'>
                Character Count: {blogData?.metaDescription?.length || 0} <span>(Recommended Characters: 160)</span>
              </span>
            </Grid>
          </Grid>
        </Card>

        {/* -------------- GENERAL SECTION -------------- */}
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
          <Typography variant="h5" fontWeight="semibold" mb={4}>
            Contents Section
          </Typography>
          <Grid container spacing={2.5}>

            <Grid item xs={12} md={6} lg={4}>
              <TextField
                label="Slug"
                fullWidth
                name="slug"
                value={blogData.slug}
                onChange={handleSlugChange}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth error={Boolean(errors.category)}>
                <InputLabel>Blog Categories</InputLabel>
                <Select
                  multiple
                  label="Blog Categories"
                  name="categories"
                  value={blogData.categories || []}
                  onChange={handleCategoryChange}
                  renderValue={(selected) => {
                    if (!Array.isArray(selected) || !localCategories) return '';
                    return selected
                      .map(slug => {
                        const category = localCategories.find(c => c.name === slug);
                        return category?.name || '';
                      })
                      .filter(name => name) // Remove any undefined/null values
                      .join(', ');
                  }}
                  error={Boolean(errors.categories)}
                >
                  {localCategories.map((c) => (
                    <MenuItem key={c.slug} value={c.name}>
                      <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                        <Box display="flex" alignItems="center">
                          {/* Checkbox only shown in dropdown, checked if value is selected */}
                          <Checkbox
                            size="small"
                            checked={blogData.categories === c.name || (Array.isArray(blogData.categories) && blogData.categories.includes(c.name))}
                            sx={{
                              pl: 0,
                              visibility: 'hidden', // Hidden by default
                              '.Mui-focused &, .MuiMenu-paper &': {
                                visibility: 'visible' // Only visible in dropdown
                              }
                            }}
                          />
                          <span>{c?.name?.toLowerCase() === "cannabis education" ? "Cannabis Education" : c.name}</span>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(openCustomModalDialog({
                              modalData: {
                                deleteHandler: (deleteItem) => handleDeleteCategory(deleteItem),
                                deleteId: c.slug,
                                deleteType: 'Category'
                              },
                              modal: 'DELETE_MODAL'
                            }));
                          }}
                          sx={{ p: 0, ml: 1, color: 'red' }}
                        >
                          {c?.name?.toLowerCase() !== "cannabis education" && <DeleteIcon width='16px' height='16px' />}
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                  <MenuItem
                    key="add_new_category"
                    sx={{ fontStyle: "italic" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenCategoryDialog(true);
                    }}
                  >
                    + Add New Category
                  </MenuItem>
                </Select>
                {errors.categories && <FormHelperText error>{errors.categories}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth error={Boolean(errors.author)}>
                <InputLabel>Author</InputLabel>
                <Select
                  label="Author"
                  fullWidth
                  name="author"
                  value={blogData?.author?.name || ""} // Display the name in the select
                  onChange={handleAuthorChange}
                  error={Boolean(errors.author)}
                  helperText={errors.author}
                  renderValue={(selected) => {
                    // Find the selected author object to display the name
                    const selectedAuthor = localAuthors?.find(a => a.name === selected);
                    return selectedAuthor?.name || "";
                  }}
                >
                  {localAuthors?.map((a) => (
                    <MenuItem key={a.slug || a._id} value={a.name} authorData={a}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <span>{a.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.author && <FormHelperText error>{errors.author}</FormHelperText>}

              </FormControl>


            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Box className='filter-price-unit'>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    className="form-select"
                    size='small'
                    value={blogData.state || ""}
                    label='State'
                    onChange={(e) => setBlogData(prev => ({ ...prev, state: e.target.value }))}
                    renderValue={(selected) => selected}
                  >
                    <ListSubheader>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search states..."
                        fullWidth
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onChange={(e) => setStateSearch(e.target.value)}
                        value={stateSearch}
                      />
                    </ListSubheader>
                    {states?.filter(state => state.label.toLowerCase().includes(stateSearch.toLowerCase())).map(data => (
                      <MenuItem key={data?.label} value={data.value}>
                        {data?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                label="Read Time (Min)"
                fullWidth
                name="readTime"
                value={blogData?.readTime || ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/\\D/g, '');
                  if (val === '' || parseInt(val) <= 60) {
                    handleChange({ target: { name: 'readTime', value: val } });
                  }
                }}
                error={Boolean(errors.readTime)}
                helperText={errors.readTime}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <TextField
                label="Excerpt"
                fullWidth
                name="excerpt"
                multiline
                rows={4}
                value={blogData.excerpt}
                onChange={handleChange}
                variant="outlined"
                className="form-input"
                inputProps={{ maxLength: 500 }}
                helperText={`${blogData.excerpt?.length || 0}/500`}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={12}>

            </Grid>
            {/* ------- IMAGE UPLOADS -------- */}
            <Grid item xs={12}>
              <AltImageSection errors={errors} formData={blogData} isListingImage={false} setFormData={setBlogData} filesize={true} validTypes={true} />
            </Grid>

            <DragDropContext onDragEnd={onContentDragEnd}>
              <Droppable droppableId="blog-contents">
                {(provided) => (
                  <Grid
                    container
                    spacing={3}
                    sx={{ p: 3 }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {/* Render visible content editors */}

                    {(Array.isArray(blogData.content) ? blogData.content : []).map((contentItem, index) => {
                      const fieldNumber = index + 1; // visible position label
                      const textKey = `text${index + 1}`;
                      const draggableId = contentItem?._id || `content-${index}`;
                      return (
                        <Draggable key={draggableId} draggableId={draggableId} index={index}>
                          {(dragProvided, snapshot) => (
                            <Grid
                              item
                              xs={12}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              sx={{
                                opacity: snapshot.isDragging ? 0.92 : 1,
                                transition: "opacity 120ms ease",
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box style={{ position: "relative" }}>
                                  <span
                                    style={{
                                      position: "absolute",
                                      width: "30px",
                                      height: "30px",
                                      color: "#656575",
                                      top: "-6px",
                                      right: "-27px",
                                      cursor: "grab",
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    {...dragProvided.dragHandleProps}
                                    onKeyDown={(e) => {
                                      // Optional keyboard support: Alt+ArrowUp/Down to move
                                      if (e.altKey && e.key === "ArrowUp") {
                                        onContentDragEnd({ source: { index }, destination: { index: Math.max(0, index - 1) } });
                                      }
                                      if (e.altKey && e.key === "ArrowDown") {
                                        onContentDragEnd({
                                          source: { index },
                                          destination: { index: Math.min((blogData.content?.length || 1) - 1, index + 1) },
                                        });
                                      }
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M8.5 7C9.32843 7 10 6.32843 10 5.5C10 4.67157 9.32843 4 8.5 4C7.67157 4 7 4.67157 7 5.5C7 6.32843 7.67157 7 8.5 7ZM8.5 13.5C9.32843 13.5 10 12.8284 10 12C10 11.1716 9.32843 10.5 8.5 10.5C7.67157 10.5 7 11.1716 7 12C7 12.8284 7.67157 13.5 8.5 13.5ZM10 18.5C10 19.3284 9.32843 20 8.5 20C7.67157 20 7 19.3284 7 18.5C7 17.6716 7.67157 17 8.5 17C9.32843 17 10 17.6716 10 18.5ZM15.5 7C16.3284 7 17 6.32843 17 5.5C17 4.67157 16.3284 4 15.5 4C14.6716 4 14 4.67157 14 5.5C14 6.32843 14.6716 7 15.5 7ZM17 12C17 12.8284 16.3284 13.5 15.5 13.5C14.6716 13.5 14 12.8284 14 12C14 11.1716 14.6716 10.5 15.5 10.5C16.3284 10.5 17 11.1716 17 12ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z"></path>
                                    </svg>
                                  </span>
                                  <Typography variant='body2' fontWeight='medium' color="#2E2E3A">
                                    Content
                                  </Typography>
                                  {/* drag and drop icon */}

                                </Box>

                                {/* Show remove button only if there's more than 1 editor */}
                                {(blogData.content?.length || 0) > 1 && (
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => removeContentField(index)}
                                    sx={{ minWidth: 'auto' }}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </Box>

                              <CommonQuillEditor
                                allowHeading={false}
                                style={true}
                                height={"300px"}
                                value={contentItem?.[textKey] || ""}
                                onChange={(value) => updateContentField(index, value)}
                              />

                              {index === 0 && errors.content0 && (
                                <FormHelperText error>{errors.content0}</FormHelperText>
                              )}
                            </Grid>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}

                    {/* Add More Button - only show if less than 5 editors */}
                    {(blogData.content?.length || 0) < maxContentEditors && (
                      <Grid item xs={12}>
                        <Button
                          style={{ minWidth: "240px" }}
                          type='button'
                          onClick={addContentField}
                          variant='contained'
                          color='primary'
                          startIcon={<AddIcon />}
                        >
                          Add More Contents
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
          {/* <Button
            style={{ width: "200px" }}
            type='button'
            onClick={addContentField}
            variant='contained'
            color='primary'
          >Add More Content</Button> */}
        </Card>

        {/* -------------- FAQ SECTION -------------- */}
        <Card sx={{ mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>
          <Accordion sx={{ boxShadow: "none", borderRadius: 2 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h6" fontWeight="600">FAQs</Typography>

                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={
                    <Switch
                      checked={blogData?.faqStatus}
                      onChange={(e) => setBlogData((prev) => ({ ...prev, faqStatus: e.target.checked }))}
                    />
                  }
                  label={blogData?.faqStatus ? <Typography component="span" variant="body2" fontWeight="600">Visible</Typography> : <Typography component="span" variant="body2" fontWeight="600" color="secondary">Hidden</Typography>}
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <FAQFormList
                faqs={blogData.faqs}
                onChange={handleChangeFAQ}
                onRemove={handleRemoveFAQ}
                onAdd={addFAQHandler}
              />
            </AccordionDetails>
          </Accordion>

        </Card>
        <Card sx={{ mb: 2, borderRadius: 2.5, boxShadow: 'none' }}>

          <Box display="flex" alignItems="center" gap={2} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="600">Related Blogs</Typography>

            <FormControlLabel
              onClick={(e) => e.stopPropagation()}
              control={
                <Switch
                  checked={blogData?.relatedBlogStatus}
                  onChange={(e) => setBlogData((prev) => ({ ...prev, relatedBlogStatus: e.target.checked }))}
                />
              }
              label={blogData?.relatedBlogStatus ? <Typography component="span" variant="body2" fontWeight="600">Custom</Typography> : <Typography component="span" variant="body2" fontWeight="600" color="secondary">Default</Typography>}
            />
          </Box>
          {
            blogData?.relatedBlogStatus &&
            <Box px={2} pb={2}>
              <InfiniteScrollListBlog
                dataFetcher={getBlogList}
                optionSelectHandler={blogSelectHandler}
                options={optionsList}
                value={
                  Array.isArray(blogData?.relatedBlogData)
                    ? blogData.relatedBlogData.map((b) =>
                      typeof b === "object" && b !== null ? b.blogId : b
                    )
                    : blogData?.relatedBlogData
                }
                label='Select Related Blog'
              />
            </Box>
          }


        </Card>

        {/* Add Category Dialog */}
        <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)}>
          <DialogTitle>Add New Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenCategoryDialog(false)}
              color="inherit"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddNewCategory}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Add Category

            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
